import React, { useState, useMemo } from 'react';
import {
    Signal, Lightbulb, Type, Mic, Video, Trash2, Plus,
    ChevronUp, ChevronDown, Youtube, Upload, Award, Layers
} from 'lucide-react';
import InlineMathField from '../math/InlineMathField';
import { MathRenderer } from '../math/MathRenderer';
import { VoiceRecorder } from '../shared/VoiceRecorder';

export const DIFFICULTY_LEVELS = [
    { value: 1, label: 'Level 1', color: 'bg-green-500', description: 'Basic' },
    { value: 2, label: 'Level 2', color: 'bg-yellow-500', description: 'Intermediate' },
    { value: 3, label: 'Level 3', color: 'bg-orange-500', description: 'Advanced' },
    { value: 4, label: 'Level 4', color: 'bg-red-500', description: 'Expert' }
];

// Difficulty Level Selector Component
export const DifficultySelector = ({ value, onChange, compact = false }) => (
    <div className={compact ? 'mb-2' : 'mb-4'}>
        <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
            <Signal size={14} className="text-indigo-400" />
            Difficulty Level
        </label>
        <div className="flex gap-1">
            {DIFFICULTY_LEVELS.map((level) => (
                <button
                    key={level.value}
                    type="button"
                    onClick={() => onChange(level.value)}
                    className={`flex-1 py-1.5 px-2 rounded-lg border-2 transition-all ${value === level.value
                        ? `${level.color} border-transparent text-white font-semibold`
                        : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-slate-500'
                        }`}
                >
                    <div className="text-xs font-medium">{level.value}</div>
                    {!compact && <div className="text-xs opacity-80">{level.description}</div>}
                </button>
            ))}
        </div>
    </div>
);

// Hint Input Component
export const HintSection = ({ value, onChange, bookId, compact = false }) => {
    const [activeTab, setActiveTab] = useState('text');
    const [showRecorder, setShowRecorder] = useState(false);
    const [videoType, setVideoType] = useState('youtube');
    const [youtubeUrl, setYoutubeUrl] = useState(value.video?.type === 'youtube' ? value.video.url : '');

    const hasText = value.text?.trim();
    const hasAudio = value.audio?.url;
    const hasVideo = value.video?.url;
    const hintCount = [hasText, hasAudio, hasVideo].filter(Boolean).length;

    const getYoutubeId = (url) => {
        const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
        return match ? match[1] : null;
    };

    const handleAudioSave = (voiceData) => {
        onChange({
            ...value,
            audio: {
                blob: voiceData.audioBlob,
                duration: voiceData.duration,
                speaker: voiceData.speaker,
                url: null
            }
        });
        setShowRecorder(false);
    };

    const handleRemoveAudio = () => onChange({ ...value, audio: null });

    const handleYoutubeSubmit = () => {
        const videoId = getYoutubeId(youtubeUrl);
        if (videoId) {
            onChange({
                ...value,
                video: {
                    type: 'youtube',
                    url: youtubeUrl,
                    videoId: videoId,
                    thumbnail: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
                }
            });
        }
    };

    const handleVideoUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 100 * 1024 * 1024) {
                // We'll need to pass an error handling function if we want to show errors
                console.error('Video must be less than 100MB');
                return;
            }
            onChange({
                ...value,
                video: {
                    type: 'upload',
                    file: file,
                    url: URL.createObjectURL(file),
                    name: file.name
                }
            });
        }
    };

    const handleRemoveVideo = () => {
        onChange({ ...value, video: null });
        setYoutubeUrl('');
    };

    return (
        <div className={compact ? 'mb-2' : 'mb-4'}>
            <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <Lightbulb size={14} className="text-yellow-400" />
                Hints
                {hintCount > 0 && (
                    <span className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                        {hintCount}
                    </span>
                )}
            </label>

            <div className="flex gap-1 mb-2 bg-slate-700/50 p-1 rounded-lg">
                {[
                    { id: 'text', icon: Type, label: 'Text', active: hasText },
                    { id: 'audio', icon: Mic, label: 'Audio', active: hasAudio },
                    { id: 'video', icon: Video, label: 'Video', active: hasVideo }
                ].map(tab => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${activeTab === tab.id
                            ? 'bg-slate-600 text-white shadow'
                            : 'text-slate-400 hover:text-slate-200'
                            }`}
                    >
                        <tab.icon size={12} />
                        {tab.label}
                        {tab.active && <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full" />}
                    </button>
                ))}
            </div>

            {activeTab === 'text' && (
                <InlineMathField
                    value={value.text || ''}
                    onChange={(text) => onChange({ ...value, text })}
                    rows={2}
                    className="w-full bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-yellow-500/50"
                    placeholder="Add hint... (Ctrl+M for math)"
                />
            )}

            {activeTab === 'audio' && (
                <div>
                    {hasAudio ? (
                        <div className="bg-slate-700/50 rounded-lg p-2 border border-slate-600">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-slate-300 flex items-center gap-1">
                                    <Mic size={12} className="text-purple-400" /> Audio Hint
                                </span>
                                <button type="button" onClick={handleRemoveAudio} className="p-1 text-red-400 hover:text-red-300">
                                    <Trash2 size={12} />
                                </button>
                            </div>
                            {value.audio.url && <audio controls className="w-full h-6" src={value.audio.url} />}
                        </div>
                    ) : showRecorder ? (
                        <div className="bg-slate-700/50 rounded-lg border border-slate-600 overflow-hidden">
                            <VoiceRecorder level="media" onSave={handleAudioSave} onCancel={() => setShowRecorder(false)} />
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setShowRecorder(true)}
                            className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-purple-500 hover:text-purple-400 transition text-sm"
                        >
                            <Mic size={16} /> Record Audio
                        </button>
                    )}
                </div>
            )}

            {activeTab === 'video' && (
                <div>
                    {hasVideo ? (
                        <div className="bg-slate-700/50 rounded-lg p-2 border border-slate-600">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-slate-300 flex items-center gap-1">
                                    {value.video.type === 'youtube' ? <Youtube size={12} className="text-red-400" /> : <Video size={12} className="text-blue-400" />}
                                    {value.video.type === 'youtube' ? 'YouTube' : value.video.name}
                                </span>
                                <button type="button" onClick={handleRemoveVideo} className="p-1 text-red-400 hover:text-red-300">
                                    <Trash2 size={12} />
                                </button>
                            </div>
                            {value.video.type === 'youtube' && value.video.thumbnail && (
                                <img src={value.video.thumbnail} alt="Thumbnail" className="w-full h-20 object-cover rounded" />
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <div className="flex gap-1 bg-slate-700/30 p-1 rounded-lg">
                                <button
                                    type="button"
                                    onClick={() => setVideoType('youtube')}
                                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-xs transition ${videoType === 'youtube' ? 'bg-red-600/20 text-red-400' : 'text-slate-400 hover:text-white'
                                        }`}
                                >
                                    <Youtube size={12} /> YouTube
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setVideoType('upload')}
                                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-xs transition ${videoType === 'upload' ? 'bg-blue-600/20 text-blue-400' : 'text-slate-400 hover:text-white'
                                        }`}
                                >
                                    <Upload size={12} /> Upload
                                </button>
                            </div>
                            {videoType === 'youtube' ? (
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={youtubeUrl}
                                        onChange={(e) => setYoutubeUrl(e.target.value)}
                                        placeholder="YouTube URL"
                                        className="flex-1 bg-slate-700 text-sm border border-slate-600 rounded-md p-2 text-slate-100"
                                    />
                                    <button type="button" onClick={handleYoutubeSubmit} className="px-3 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm">
                                        Add
                                    </button>
                                </div>
                            ) : (
                                <label className="flex items-center justify-center w-full p-3 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-blue-500 text-slate-400 hover:text-blue-400 transition text-sm">
                                    <Upload size={16} className="mr-2" /> Upload Video
                                    <input type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
                                </label>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Marks Input Component
export const MarksInput = ({ value, onChange, compact = false }) => (
    <div>
        <label className="block text-sm font-medium text-slate-300 mb-1 flex items-center gap-2">
            <Award size={14} className="text-amber-400" />
            Marks
        </label>
        <input
            type="number"
            min="1"
            max="100"
            value={value}
            onChange={(e) => onChange(Number(e.target.value) || 1)}
            className={`w-full bg-slate-700 border border-slate-600 rounded-md ${compact ? 'p-1.5 text-sm' : 'p-2'} text-slate-100 focus:ring-2 focus:ring-amber-500`}
        />
    </div>
);

// Knowledge Input Component
export const KnowledgeInput = ({ value, onChange, compact = false }) => {
    const [skillInput, setSkillInput] = useState('');
    const [conceptInput, setConceptInput] = useState('');

    const addSkill = () => {
        if (skillInput.trim() && !value.skills.includes(skillInput.trim())) {
            onChange({ ...value, skills: [...value.skills, skillInput.trim()] });
            setSkillInput('');
        }
    };

    const addConcept = () => {
        if (conceptInput.trim() && !value.concepts.includes(conceptInput.trim())) {
            onChange({ ...value, concepts: [...value.concepts, conceptInput.trim()] });
            setConceptInput('');
        }
    };

    const removeSkill = (skill) => onChange({ ...value, skills: value.skills.filter(s => s !== skill) });
    const removeConcept = (concept) => onChange({ ...value, concepts: value.concepts.filter(c => c !== concept) });

    return (
        <div className={`${compact ? 'p-2' : 'p-3'} bg-slate-700/30 rounded-lg border border-slate-600`}>
            <label className="block text-sm font-medium text-slate-300 mb-2">
                Required Knowledge
            </label>

            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="block text-xs text-slate-400 mb-1">Skills</label>
                    <div className="flex gap-1 mb-1">
                        <input
                            type="text"
                            value={skillInput}
                            onChange={(e) => setSkillInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                            placeholder="Add skill"
                            className="flex-1 bg-slate-700 text-xs border border-slate-600 rounded-md p-1.5 text-slate-100"
                        />
                        <button type="button" onClick={addSkill} className="px-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs">+</button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {value.skills.map((skill, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-600/30 text-blue-300 text-xs rounded-full">
                                {skill}
                                <button type="button" onClick={() => removeSkill(skill)} className="hover:text-red-400">×</button>
                            </span>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-xs text-slate-400 mb-1">Concepts</label>
                    <div className="flex gap-1 mb-1">
                        <input
                            type="text"
                            value={conceptInput}
                            onChange={(e) => setConceptInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addConcept())}
                            placeholder="Add concept"
                            className="flex-1 bg-slate-700 text-xs border border-slate-600 rounded-md p-1.5 text-slate-100"
                        />
                        <button type="button" onClick={addConcept} className="px-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs">+</button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {value.concepts.map((concept, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-600/30 text-purple-300 text-xs rounded-full">
                                {concept}
                                <button type="button" onClick={() => removeConcept(concept)} className="hover:text-red-400">×</button>
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Subtopics Input Component
export const SubtopicsInput = ({ value = [], onChange, availableSubtopics = [], onAddNew }) => {
    const [input, setInput] = useState('');

    const allSubtopics = useMemo(() => {
        const set = new Set([...availableSubtopics, ...value]);
        return Array.from(set).sort();
    }, [availableSubtopics, value]);

    const addSubtopic = () => {
        if (input.trim() && !value.includes(input.trim())) {
            const trimmed = input.trim();
            onChange([...value, trimmed]);
            onAddNew?.(trimmed);
            setInput('');
        }
    };

    const toggleSubtopic = (subtopic) => {
        if (value.includes(subtopic)) {
            onChange(value.filter(s => s !== subtopic));
        } else {
            onChange([...value, subtopic]);
        }
    };

    return (
        <div className="mb-2">
            <label className="block text-xs text-slate-400 mb-1 flex items-center gap-1">
                <Layers size={12} className="text-teal-400" />
                Subtopics
            </label>
            {allSubtopics.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1 bg-slate-700/30 p-1.5 rounded max-h-16 overflow-y-auto">
                    {allSubtopics.map(subtopic => (
                        <button
                            key={subtopic}
                            type="button"
                            onClick={() => toggleSubtopic(subtopic)}
                            className={`px-1.5 py-0.5 rounded text-xs transition ${value.includes(subtopic)
                                ? 'bg-teal-600 text-white'
                                : 'bg-slate-600 text-slate-400 hover:bg-slate-500'
                                }`}
                        >
                            {subtopic}
                        </button>
                    ))}
                </div>
            )}
            <div className="flex gap-1">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubtopic())}
                    placeholder="Add subtopic"
                    className="flex-1 bg-slate-700 text-xs border border-slate-600 rounded-md p-1.5 text-slate-100"
                />
                <button type="button" onClick={addSubtopic} className="px-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-xs">+</button>
            </div>
        </div>
    );
};

// Multi-Grade Selector Component
export const MultiGradeSelector = ({ value = [], onChange, availableGrades = [] }) => {
    const toggleGrade = (grade) => {
        if (value.includes(grade)) {
            onChange(value.filter(g => g !== grade));
        } else {
            onChange([...value, grade]);
        }
    };

    return (
        <div className="mb-3">
            <label className="block text-sm font-medium text-slate-300 mb-2">
                Grades (Select multiple)
            </label>
            <div className="flex flex-wrap gap-2">
                {availableGrades.map(grade => (
                    <button
                        key={grade}
                        type="button"
                        onClick={() => toggleGrade(grade)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${value.includes(grade)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                    >
                        {grade}
                    </button>
                ))}
                {availableGrades.length === 0 && (
                    <span className="text-sm text-slate-400">No grades available. Create a question with a grade first.</span>
                )}
            </div>
            {value.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                    {value.map(grade => (
                        <span key={grade} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-600/30 text-indigo-300 text-xs rounded-full">
                            {grade}
                            <button type="button" onClick={() => toggleGrade(grade)} className="hover:text-red-400">×</button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};
